import React, { useState, useEffect, useRef, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Grid,
    IconButton,
    Avatar,
    Typography,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    InputBase
} from '@mui/material';
import {
    ArrowBackIosNewOutlinedIcon,
    KeyboardArrowDownOutlinedIcon,
    MoreHorizOutlinedIcon,
    DeleteOutlineIcon,
    SearchOutlinedIcon,
    CloseOutlinedIcon
} from 'icons/materialIcons';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { getSubteam, getTeamSubteams, deleteSubteam } from 'team/apis/teamSubteamApi';
import { useTeam } from 'team/contexts/teamContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import Modal from 'components/common/Modal';
import { getSubteamColor, getSubteamInitials } from 'team/shared/utils/subteamUtils';

// Styled component with persistent cursor - using CSS class
const ClickableBox = styled(Box)({
    cursor: 'pointer !important',
    '&:hover': {
        cursor: 'pointer !important'
    },
    '&:active': {
        cursor: 'pointer !important'
    },
    '&:focus': {
        cursor: 'pointer !important'
    },
    '&:focus-visible': {
        cursor: 'pointer !important'
    },
    '&:visited': {
        cursor: 'pointer !important'
    },
    '& *': {
        cursor: 'pointer !important',
        '&:hover': {
            cursor: 'pointer !important'
        },
        '&:active': {
            cursor: 'pointer !important'
        }
    }
});

const TeamSubteamMembersNavbar = ({ subteamId }) => {
    const navigate = useNavigate();
    const { currentTeamId } = useTeam();
    const [subteamData, setSubteamData] = useState(null);
    const [allSubteams, setAllSubteams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subteamDropdownOpen, setSubteamDropdownOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const subteamDropdownRef = useRef(null);
    const [subteamSearchQuery, setSubteamSearchQuery] = useState('');
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchSubteamData = useCallback(async () => {
        try {
            if (subteamId) {
                const response = await getSubteam(subteamId);
                // Handle different response formats
                const subteam = response?.subteam || response;
                if (subteam && (response?.success !== false)) {
                    console.log('[TeamSubteamMembersNavbar] Fetched subteam data:', subteam);
                    setSubteamData(subteam);
                }
            }
        } catch (error) {
            console.error('Error fetching subteam data:', error);
            setNotification({
                open: true,
                message: 'Failed to load subteam information',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [subteamId]);

    useEffect(() => {
        fetchSubteamData();
    }, [fetchSubteamData]);

    // Listen for subteam update events to refresh navbar
    useEffect(() => {
        const handleSubteamUpdate = async (event) => {
            if (event.detail && event.detail.subteamId === subteamId) {
                console.log('[TeamSubteamMembersNavbar] Subteam updated, refreshing data...');
                // Refresh current subteam data
                await fetchSubteamData();
                // Also refresh the subteams list for the dropdown
                if (currentTeamId) {
                    try {
                        const response = await getTeamSubteams(currentTeamId);
                        if (response && response.success && response.subteams) {
                            setAllSubteams(response.subteams);
                        }
                    } catch (error) {
                        console.error('Error refreshing subteams list:', error);
                    }
                }
            }
        };

        window.addEventListener('subteamUpdated', handleSubteamUpdate);
        return () => {
            window.removeEventListener('subteamUpdated', handleSubteamUpdate);
        };
    }, [subteamId, fetchSubteamData, currentTeamId]);

    useEffect(() => {
        const fetchAllSubteams = async () => {
            try {
                if (currentTeamId) {
                    const response = await getTeamSubteams(currentTeamId);
                    if (response && response.success && response.subteams) {
                        setAllSubteams(response.subteams);
                    }
                }
            } catch (error) {
                console.error('Error fetching all subteams:', error);
            }
        };

        fetchAllSubteams();
    }, [currentTeamId]);

    const handleBackClick = () => {
        navigate('/team/subteams');
    };

    const handleSubteamDropdownClick = (e) => {
        if (e) {
            e.stopPropagation();
        }
        console.log('[TeamSubteamMembersNavbar] Opening dropdown');
        setSubteamDropdownOpen(true);
    };

    const handleSubteamDropdownClose = () => {
        setSubteamDropdownOpen(false);
        setSubteamSearchQuery(''); // Clear search when closing
    };

    // Ensure cursor stays as pointer on the dropdown trigger
    useEffect(() => {
        if (subteamDropdownRef.current) {
            const element = subteamDropdownRef.current;

            // Create a style element to inject CSS
            const styleId = 'subteam-dropdown-cursor-style';
            let styleElement = document.getElementById(styleId);
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = styleId;
                document.head.appendChild(styleElement);
            }

            // Get a unique class name based on the element
            const className = `subteam-dropdown-cursor-${Date.now()}`;
            element.classList.add(className);

            // Inject CSS that forces cursor to pointer
            styleElement.textContent += `
                .${className},
                .${className} *,
                .${className}:hover,
                .${className}:active,
                .${className}:focus,
                .${className}:focus-visible {
                    cursor: pointer !important;
                }
            `;

            const setCursor = () => {
                if (element) {
                    element.style.setProperty('cursor', 'pointer', 'important');
                }
            };

            // Set cursor on various events
            element.addEventListener('mouseenter', setCursor);
            element.addEventListener('mouseleave', setCursor);
            element.addEventListener('mousedown', setCursor);
            element.addEventListener('mouseup', setCursor);
            element.addEventListener('mousemove', setCursor);
            element.addEventListener('click', setCursor);

            return () => {
                element.removeEventListener('mouseenter', setCursor);
                element.removeEventListener('mouseleave', setCursor);
                element.removeEventListener('mousedown', setCursor);
                element.removeEventListener('mouseup', setCursor);
                element.removeEventListener('mousemove', setCursor);
                element.removeEventListener('click', setCursor);
                element.classList.remove(className);
            };
        }
    }, [subteamDropdownOpen]);

    // Filter subteams based on search query
    const filteredSubteams = allSubteams.filter(subteam => {
        if (!subteamSearchQuery) return true;
        const query = subteamSearchQuery.toLowerCase();
        return (
            subteam.name?.toLowerCase().includes(query) ||
            subteam.description?.toLowerCase().includes(query)
        );
    });

    const handleSubteamSelect = (selectedSubteamId) => {
        navigate(`/team/subteams/${selectedSubteamId}/members`);
        handleSubteamDropdownClose();
    };

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleViewSubteamMembers = () => {
        // Navigate to team members page filtered by subteam
        navigate(`/team/members?subteamId=${subteamId}`);
    };


    const handleNotificationClose = () => {
        setNotification({ ...notification, open: false });
    };

    const handleRemoveClick = () => {
        handleMenuClose();
        setRemoveModalOpen(true);
    };

    const handleRemoveModalClose = () => {
        if (!isDeleting) {
            setRemoveModalOpen(false);
        }
    };

    const handleConfirmRemove = async () => {
        if (!subteamId || isDeleting) return;

        setIsDeleting(true);
        try {
            const response = await deleteSubteam(subteamId);

            if (response && response.success) {
                setNotification({
                    open: true,
                    message: 'Subteam removed successfully',
                    severity: 'success'
                });

                // Navigate to subteams list after successful deletion
                setTimeout(() => {
                    navigate('/team/subteams');
                }, 500);
            } else {
                throw new Error(response?.error || 'Failed to delete subteam');
            }
        } catch (error) {
            console.error('Error deleting subteam:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to remove subteam. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsDeleting(false);
            setRemoveModalOpen(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ padding: 2 }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ padding: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                                <IconButton
                                    sx={{
                                        backgroundColor: 'transparent',
                                        color: '#828282',
                                        padding: 0,
                                    }}
                                    onClick={handleBackClick}
                                >
                                    <ArrowBackIosNewOutlinedIcon />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <Box
                                    sx={{
                                        height: 25,
                                        width: '1px',
                                        backgroundColor: '#e0e0e0',
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                        src={subteamData?.logoUrl}
                                        alt={subteamData?.name || 'Subteam'}
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            border: '2px solid #ffffff',
                                            backgroundColor: subteamData?.logoUrl ? 'transparent' : getSubteamColor(subteamData?._id, subteamData?.name),
                                        }}
                                    >
                                        {!subteamData?.logoUrl && getSubteamInitials(subteamData?.name)}
                                    </Avatar>
                                    <Box sx={{ marginLeft: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ClickableBox
                                            ref={subteamDropdownRef}
                                            onClick={handleSubteamDropdownClick}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.cursor = 'pointer';
                                                e.currentTarget.style.setProperty('cursor', 'pointer', 'important');
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.cursor = 'pointer';
                                                e.currentTarget.style.setProperty('cursor', 'pointer', 'important');
                                            }}
                                            onMouseMove={(e) => {
                                                e.currentTarget.style.cursor = 'pointer';
                                                e.currentTarget.style.setProperty('cursor', 'pointer', 'important');
                                            }}
                                            onMouseDown={(e) => {
                                                e.currentTarget.style.cursor = 'pointer';
                                                e.currentTarget.style.setProperty('cursor', 'pointer', 'important');
                                            }}
                                            onMouseUp={(e) => {
                                                e.currentTarget.style.cursor = 'pointer';
                                                e.currentTarget.style.setProperty('cursor', 'pointer', 'important');
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                style={{ cursor: 'pointer' }}
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 600,
                                                    color: '#333',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    paddingRight: '4px',
                                                    '&:hover': {
                                                        cursor: 'pointer'
                                                    },
                                                    '&:active': {
                                                        cursor: 'pointer'
                                                    },
                                                    '&:focus': {
                                                        cursor: 'pointer'
                                                    }
                                                }}
                                            >
                                                {subteamData?.name || 'Subteam'}
                                            </Typography>
                                            <Box
                                                component="span"
                                                onClick={(e) => {
                                                    console.log('[TeamSubteamMembersNavbar] Arrow clicked');
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleSubteamDropdownClick(e);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    marginLeft: '-4px',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    '&:hover': {
                                                        cursor: 'pointer'
                                                    },
                                                    '&:active': {
                                                        cursor: 'pointer'
                                                    },
                                                    '&:focus': {
                                                        cursor: 'pointer'
                                                    }
                                                }}
                                            >
                                                <KeyboardArrowDownOutlinedIcon
                                                    sx={{
                                                        color: '#828282',
                                                        fontSize: '18px',
                                                        cursor: 'pointer',
                                                        pointerEvents: 'none',
                                                        transform: subteamDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.3s ease-in-out'
                                                    }}
                                                />
                                            </Box>
                                        </ClickableBox>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm container justifyContent="flex-end" spacing={2}>
                        <Grid item xs={12} sm="auto">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TeamDarkButtonWithIcon
                                    text="View Subteam Members"
                                    icon={<GroupOutlinedIcon />}
                                    iconPosition="start"
                                    onClick={handleViewSubteamMembers}
                                />

                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#fff',
                                        color: '#cccccc',
                                        fontWeight: '500',
                                        borderRadius: '8px',
                                        border: '1px #e0e0e0 solid',
                                        textTransform: 'none',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '75px',
                                        padding: '6.5px 16px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#fff',
                                            color: '#828282',
                                            boxShadow: 'none',
                                        },
                                    }}
                                    onClick={handleMenuOpen}
                                >
                                    <MoreHorizOutlinedIcon sx={{ color: '#828282' }} />
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            {/* Subteam Dropdown Menu */}
            {subteamDropdownRef.current && (
                <Menu
                    anchorEl={subteamDropdownRef.current}
                    open={subteamDropdownOpen}
                    onClose={handleSubteamDropdownClose}
                    disableAutoFocusItem
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                            width: {
                                xs: '70vw',
                                sm: '320px',
                                md: '350px'
                            },
                            maxWidth: '350px',
                            maxHeight: '80vh',
                            overflow: 'hidden',
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#fafafa'
                        }
                    }}
                    MenuListProps={{
                        sx: {
                            padding: 0
                        }
                    }}
                    slotProps={{
                        backdrop: {
                            sx: {
                                cursor: 'default'
                            }
                        }
                    }}
                >
                    {/* Search Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            padding: '8px 16px',
                            marginBottom: '12px',
                            '&:hover': {
                                boxShadow: 2,
                            },
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                color: '#828282',
                                fontSize: '18px',
                                marginRight: '8px'
                            }}
                        />
                        <InputBase
                            fullWidth
                            value={subteamSearchQuery}
                            onChange={(e) => setSubteamSearchQuery(e.target.value)}
                            placeholder="Search..."
                            sx={{
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#828282',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {filteredSubteams.length === 0 ? (
                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                    No subteams found
                                </Typography>
                            </Box>
                        ) : (
                            filteredSubteams.map((subteam) => (
                                <MenuItem
                                    key={subteam._id}
                                    onClick={() => handleSubteamSelect(subteam._id)}
                                    selected={subteam._id === subteamId}
                                    sx={{
                                        borderRadius: '8px',
                                        marginBottom: '4px',
                                        '&.Mui-selected': {
                                            backgroundColor: '#f0f0f0',
                                            '&:hover': {
                                                backgroundColor: '#e0e0e0'
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                        <Avatar
                                            src={subteam.logoUrl}
                                            alt={subteam.name}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                            }}
                                        >
                                            {!subteam.logoUrl && getSubteamInitials(subteam.name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                                {subteam.name}
                                            </Typography>
                                            {subteam.description && (
                                                <Typography sx={{ fontSize: '0.8rem', color: '#828282', marginTop: '2px' }}>
                                                    {subteam.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Box>
                </Menu>
            )}

            {/* Three dots menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        minWidth: '200px',
                        marginTop: '12px'
                    }
                }}
            >
                <MenuItem
                    onClick={handleRemoveClick}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText primary="Remove" />
                </MenuItem>
            </Menu>

            {/* Remove Subteam Confirmation Modal */}
            <Modal
                open={removeModalOpen}
                onClose={handleRemoveModalClose}
                width={{ xs: '90%', sm: '60%', md: '40%' }}
                height="auto"
                borderRadius={8}
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 3 },
                            paddingBottom: { xs: 1, sm: 1.5 }
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                color: '#333'
                            }}
                        >
                            Remove Subteam
                        </Typography>
                        <IconButton
                            onClick={handleRemoveModalClose}
                            disabled={isDeleting}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                },
                                '&:disabled': {
                                    opacity: 0.5
                                }
                            }}
                        >
                            <CloseOutlinedIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                color: '#4f4f4f',
                                marginBottom: '2rem',
                                lineHeight: 1.5
                            }}
                        >
                            Are you sure you want to remove this Subteam? This Subteam will be permanently deleted. All the members within it will remain in your Team.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleRemoveModalClose}
                                disabled={isDeleting}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#333',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        borderColor: '#b0b0b0',
                                        backgroundColor: '#f5f5f5'
                                    },
                                    '&:disabled': {
                                        opacity: 0.5
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirmRemove}
                                disabled={isDeleting}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        backgroundColor: '#333',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                            >
                                {isDeleting ? 'Removing...' : 'Yes, remove'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={handleNotificationClose}
            />
        </>
    );
};

export default TeamSubteamMembersNavbar;

